import React, { useState } from 'react';
import icons from '../../assets/icons';
import styles from './UploadFile.module.scss';
import { useDropzone } from 'react-dropzone';
import { ACCEPTABLE_IMAGE_TYPE_REQUEST, ALLOWED_MIME_TYPES } from '../../constants/constants';
import { checkFileTypes } from '../../screens/Requests/Sections/helper';
import cx from 'classnames';
import { removeExifFromImage, removeExifFromMultipleFiles } from '../../helpers/jsHelper';
import { warningToast } from '../../helpers/toastHelper';

function UploadFile({
  id,
  itemID = '',
  title,
  className,
  subClassName,
  subtitle = 'Drag an image here',
  uploadText = 'Upload from your device',
  acceptMutipleFiles = false,
  onFileChange,
  allowed_files = ACCEPTABLE_IMAGE_TYPE_REQUEST,
  allowedFileErrorMessage = 'Only images are allowed',
  children,
  hideIcon,
  hideUploadText,
  hidden,
  style,
  allowAllFiles = false,
  hideSubtitle = false,
  isDefault = false,
}) {
  const { RequestFileUploadIcon } = icons;

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    // Always accept all dropped files at the dropzone level.
    // When multiple: false is set, useDropzone rejects the ENTIRE drop if >1 file is dragged,
    // so we set multiple: true here and enforce single/multiple manually in onDrop.
    multiple: true,
    onDrop: (acceptedFiles) => {
      // For single-upload zones: silently pick only the first file even if multiple are dropped
      const filesToProcess = acceptMutipleFiles ? acceptedFiles : acceptedFiles.slice(0, 1);
      if (!filesToProcess.length) return;
      if (!acceptMutipleFiles && acceptedFiles.length > 1) {
        warningToast('Only 1 image allowed. The first image has been selected.');
      }
      if (allowAllFiles) handleFileChange(filesToProcess);
      else if (checkFileTypes(filesToProcess, allowed_files, allowedFileErrorMessage)) {
        handleFileChange(filesToProcess);
      }
    },
  });

  const handleFileChange = async (file) => {
    if (!file || (Array.isArray(file) && file.length === 0)) return;
    try {
      const singleFile = Array.isArray(file) ? file[0] : file;
      const files = acceptMutipleFiles
        ? await removeExifFromMultipleFiles(file)
        : await removeExifFromImage(singleFile);
      if (onFileChange) {
        onFileChange(files, title, (itemID = itemID), isDefault);
      }
    } catch (error) {
      console.error('UploadFile handleFileChange error:', error);
    }
  };

  return (
    <div className={className} hidden={hidden} style={style}>
      <div
        className={cx(styles.wrapper, children && styles.noborder, subClassName)}
        {...getRootProps()}>
        {!children && (
          <>
            {!hideIcon && <RequestFileUploadIcon />}
            <span className={styles.title}>{title}</span>
            {!hideSubtitle && <span className={styles.subTitle}>{subtitle}</span>}
            {!hideUploadText && <span className={styles.uploadText}>{uploadText}</span>}
          </>
        )}
        {/* multiple attr controls the file-picker dialog: single zones = single-select, multi zones = multi-select */}
        <input
          key={id}
          id={`file-upload ${id}`}
          type="file"
          {...getInputProps()}
          multiple={acceptMutipleFiles}
          onChange={(e) => {
            const selectedFiles = Array.from(e.target.files || []);
            if (!acceptMutipleFiles && selectedFiles.length > 1) {
              warningToast('Only 1 image allowed. The first image has been selected.');
            }
            // Always delegate back to dropzone's own handler (respects accept, etc.)
            getInputProps().onChange(e);
          }}
        />
        {children}
      </div>
    </div>
  );
}

export default UploadFile;
